import React, { FC } from 'react';

import Check from '@/assets/check.svg?react';
import Close from '@/assets/close.svg?react';
import Opinion from '@/components/Opinion';
import { RESPONSIVE_QUERY } from '@/consts';
import cn from '@/utils/cn';

import { useLingui } from '@lingui/react';
import { Indicator, Root as Progress } from '@radix-ui/react-progress';
import { useMediaQuery } from 'react-responsive';

export type StepBase = {
  label: string;
  required?: boolean;
  name: string;
};

export type Step = StepBase & {
  subSteps?: StepBase[];
};

interface Props {
  steps: Step[];
  currentStep: number;
  currentSubStep?: number;
  variant?: 'sub-step' | 'default';
  progress: number;
  className?: string;
  isFinished?: boolean;
  subSteps?: StepBase[];
}

const ProgressBar: FC<Props> = ({
  steps,
  currentStep,
  currentSubStep,
  variant = 'default',
  progress,
  isFinished,
  subSteps,
  className,
}) => {
  const { i18n } = useLingui();

  const isDesktop = useMediaQuery({
    query: RESPONSIVE_QUERY.IS_DESKTOP,
  });

  return (
    <div
      className={cn(
        'flex flex-row items-center gap-2 rounded-lg bg-white px-4 py-3 lg:min-w-[290px] lg:flex-col lg:items-start lg:rounded-[10px] lg:border-t-[6px] lg:border-primary lg:px-2 lg:pb-2 lg:pt-0',
        'relative z-40',
        'shadow-progress lg:shadow-none',
        className
      )}
    >
      {!isDesktop && (
        <>
          {variant === 'default' && (
            <StepUI
              step={currentStep}
              label={
                steps[currentStep - 1]?.label ?? i18n.t('Ustalenia końcowe')
              }
              nextStep={steps[currentStep]?.label}
              required={steps[currentStep - 1]?.required}
              checked={isFinished}
              isFinished={isFinished}
              subSteps={subSteps}
              className="lg:hidden"
            />
          )}
          {variant === 'sub-step' &&
            currentSubStep &&
            steps[currentStep - 1]?.subSteps && (
              <StepUI
                step={currentSubStep}
                label={
                  steps[currentStep - 1].subSteps?.[currentSubStep - 1]
                    ?.label ?? ''
                }
                nextStep={
                  steps[currentStep - 1]?.subSteps?.[currentSubStep]?.label
                }
                required={
                  steps[currentStep - 1]?.subSteps?.[currentSubStep - 1]
                    ?.required
                }
                checked={isFinished}
                isFinished={isFinished}
                className="lg:hidden"
                subSteps={subSteps}
              />
            )}
          <Progress
            value={progress}
            className="relative h-1.5 w-full flex-1 overflow-hidden rounded-full bg-dark-gray-100 lg:hidden"
          >
            <Indicator
              className="absolute size-full rounded-full bg-primary transition-transform duration-300 ease-in-out"
              style={{ transform: `translateX(-${100 - progress}%)` }}
            />
          </Progress>
        </>
      )}
      {isDesktop && (
        <>
          <div
            className={cn(
              'hidden w-full flex-col gap-3 px-3 pb-2.5 pt-[18px] lg:flex',
              variant === 'default' && 'gap-2'
            )}
          >
            {steps.map((item, index) => {
              if (variant === 'default' || index + 1 <= currentStep) {
                return (
                  <Step
                    key={index}
                    step={index + 1}
                    label={item.label}
                    required={item.required}
                    subSteps={item.subSteps}
                    currentStep={currentStep}
                    currentSubStep={currentSubStep}
                    progress={progress}
                    nextStep={steps[index + 1]?.label}
                    variant={variant}
                    isFinished={isFinished}
                  />
                );
              }
            })}
          </div>
          <Opinion className="hidden lg:flex" />
        </>
      )}
    </div>
  );
};

export default ProgressBar;

const Step = ({
  step,
  label,
  subSteps,
  nextStep,
  currentStep,
  currentSubStep,
  required,
  className,
  variant = 'sub-step',
  progress,
  isFinished,
}: {
  step: number;
  label: string;
  subSteps?: StepBase[];
  nextStep?: string;
  required?: boolean;
  currentStep: number;
  currentSubStep?: number;
  className?: string;
  variant?: 'sub-step' | 'default';
  progress?: number;
  isFinished?: boolean;
}) => {
  const stepProgress =
    currentStep === step ? progress ?? 0 : currentStep > step ? 100 : 0;

  return (
    <>
      <div className={className}>
        <StepUI
          step={step}
          unActive={!!currentStep && step > currentStep}
          label={label}
          checked={(!!currentStep && step < currentStep) || isFinished}
          required={required}
          nextStep={nextStep}
          isFinished={isFinished}
        />
      </div>
      {variant === 'default' &&
        !!currentStep &&
        nextStep &&
        progress !== undefined && (
          <div className={currentStep === step ? 'h-11' : 'h-6'}>
            <Progress
              value={progress}
              className={cn(
                'relative hidden h-0.5 flex-1 rotate-90 overflow-hidden rounded-full bg-dark-gray-100 lg:flex',
                currentStep === step
                  ? 'max-w-[44px] translate-x-[-11px] translate-y-[22px]'
                  : 'max-w-[24px] translate-y-[11px]'
              )}
            >
              <Indicator
                className="absolute size-full rounded-full bg-primary"
                style={{ transform: `translateX(-${100 - stepProgress}%)` }}
              />
            </Progress>
          </div>
        )}
      {subSteps && variant === 'sub-step' && (
        <div data-testid="sub-progress" className="flex flex-col gap-3 pl-2">
          {subSteps.map((item, index) => (
            <StepUI
              key={index}
              step={index + 1}
              label={item.label}
              unActive={!!currentSubStep && index + 1 > currentSubStep}
              checked={!!currentSubStep && index + 1 < currentSubStep}
              required={item.required}
              subSteps={subSteps}
            />
          ))}
        </div>
      )}
    </>
  );
};

export const StepUI = ({
  step,
  label,
  required,
  className,
  unActive,
  checked,
  unchecked,
  nextStep,
  isFinished,
  subSteps,
}: {
  step: number;
  label: string;
  required?: boolean;
  className?: string;
  unActive?: boolean;
  checked?: boolean;
  unchecked?: boolean;
  nextStep?: string;
  isFinished?: boolean;
  subSteps?: StepBase[];
}) => {
  const { i18n } = useLingui();

  return (
    <div
      className={cn(
        'flex flex-[1.5] items-center gap-1.5 lg:gap-2.5',
        className
      )}
    >
      <div
        className={cn(
          'special-step flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-white',
          subSteps && 'bg-dark-gray-500',
          unActive && 'bg-dark-gray-100'
        )}
      >
        {checked && <Check width={16} />}
        {unchecked && <Close width={14} />}
        {!checked && !unchecked && step}
      </div>
      <div className="flex min-h-[30px] flex-col justify-center xxs:min-w-[170px] lg:min-h-0">
        <p
          className={cn(
            'caption lg:caption-large my-auto font-jakarta text-dark-gray-400',
            unActive && 'text-dark-gray-200'
          )}
        >
          {label}
          {required && <sup className="text-red-400">*</sup>}
        </p>

        {!isFinished && nextStep && (
          <p className="caption-small text-dark-gray-300 lg:hidden">
            {i18n.t('Dalej:')} {nextStep}
          </p>
        )}
      </div>
    </div>
  );
};
